import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarCheck } from 'lucide-react';
import { USER_ROLES, type AttendanceRecord, type UserRole } from '@tailoring-crm/shared';
import { AttendanceHistoryDialog } from '../components/attendance/attendance-history-dialog.js';
import { AttendanceLedgerPanel } from '../components/attendance/attendance-ledger-panel.js';
import { LeaveRequestsPanel } from '../components/attendance/leave-requests-panel.js';
import { AttendanceSummaryTiles } from '../components/attendance/attendance-summary-tiles.js';
import { AttendanceTable } from '../components/attendance/attendance-table.js';
import { MarkLeaveDialog } from '../components/attendance/mark-leave-dialog.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { todayInputValue } from '../lib/attendance/attendance-day.js';
import { useAttendanceSearch, useCheckIn, useCheckOut } from '../lib/attendance/use-attendance.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useStaffList } from '../lib/staff/use-staff.js';

/**
 * Who is in the shop today.
 *
 * The screen is a DAY, not a feed: one date at the top, and everything below it answers for that
 * date. A shop asking "is Lakshmi in yet?" at half past ten and "who worked yesterday?" on payday
 * is asking the same question about two dates, and a list that mixed them would answer neither.
 *
 * The four tiles read the server's own counts for the whole day rather than counting the rows
 * below them — see `AttendanceSummaryTiles` for why that distinction is load-bearing — and the
 * hours column renders the minutes the server computed. Nothing on this screen subtracts one
 * timestamp from another.
 *
 * A TAILOR MAY OPEN IT. The API answers them with their own row and nothing else, and this screen
 * matches that: no buttons, no tiles, no role filter. Those describe a whole shop that is not
 * theirs, and a karigar checking whether they are marked in should not be shown the means to mark
 * anybody else. Any other role state gets the refusal, because a URL can be typed.
 */

const PAGE_SIZE = 25;

/**
 * The three questions this screen answers, and they are genuinely three.
 *
 * `roster` is "is Lakshmi in yet?" — one date, everybody. `ledger` is "how much overtime did we
 * run up last month?" — a range, and unanswerable one day at a time. `requests` is "who has
 * asked for time off?" — no date at all, a queue.
 *
 * One screen with a switch rather than three entries in the navigation, because they are all
 * the same conversation about the same people and a shop reaches them in the same moment.
 */
const VIEWS = ['roster', 'ledger', 'requests'] as const;
type AttendanceView = (typeof VIEWS)[number];

/** The last thirty days — the range the ledger opens on, and the one a wage question uses. */
function defaultLedgerRange(day: string): { from: string; to: string } {
  const to = new Date(`${day}T00:00:00Z`);
  const from = new Date(to.getTime() - 29 * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: day };
}

export function AttendancePage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const { toast } = useToast();

  const [view, setView] = useState<AttendanceView>('roster');
  const [day, setDay] = useState(todayInputValue);
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  /**
   * Grown rather than paged. Somebody looking down a roll call is looking for one person; making
   * them remember which page that was on is a worse answer than lengthening the list.
   */
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [leaveFor, setLeaveFor] = useState<AttendanceRecord | null>(null);
  const [historyFor, setHistoryFor] = useState<AttendanceRecord | null>(null);
  /** Who a write is in flight for, so a second press does not send a second arrival. */
  const [busyStaffUserId, setBusyStaffUserId] = useState<string | null>(null);

  const canWrite = role === 'owner' || role === 'manager';
  const mayRead = canWrite || role === 'tailor';

  const params = useMemo(
    () => ({
      day,
      ...(roleFilter ? { role: [roleFilter] } : {}),
      page: 1,
      pageSize,
    }),
    [day, roleFilter, pageSize],
  );

  // Not asked for at all by somebody the API would refuse — see the hook's `enabled`.
  const attendance = useAttendanceSearch(params, { enabled: mayRead });
  /**
   * The roster, for the ledger's person picker. Asked for only by somebody who may write one —
   * a karigar has nobody to pick and the endpoint would answer them with themselves anyway.
   */
  const staff = useStaffList();
  const roster = canWrite ? (staff.data?.items.filter((member) => member.active) ?? []) : [];
  const ledgerRange = defaultLedgerRange(day);

  const checkIn = useCheckIn();
  const checkOut = useCheckOut();

  const write = (staffUserId: string, run: () => Promise<unknown>, successKey: string): void => {
    setBusyStaffUserId(staffUserId);
    void (async () => {
      try {
        await run();
        toast({ title: t(successKey), variant: 'success' });
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setBusyStaffUserId(null);
      }
    })();
  };

  if (!mayRead) {
    return (
      <>
        <PageHeader title={t('attendance.title')} />
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('attendance.refused')}
        </p>
      </>
    );
  }

  const items = attendance.data?.items ?? [];
  const total = attendance.data?.total ?? 0;
  const summary = attendance.data?.summary;

  return (
    <>
      <PageHeader title={t('attendance.title')} description={t('attendance.description')} />

      <div className="flex flex-col gap-4">
        {/*
          The switch between the three questions. Chips rather than a tab bar, matching the
          filter chips elsewhere in this product, and always all three: a karigar reaches the
          ledger and the request queue too — they are their own hours and their own leave, and
          the API narrows both to them.
        */}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('attendance.viewLabel')}>
          {VIEWS.map((one) => (
            <button
              key={one}
              type="button"
              aria-pressed={view === one}
              onClick={() => setView(one)}
              className={
                view === one
                  ? 'rounded-full bg-primary px-3 py-1.5 text-sm text-primary-foreground'
                  : 'rounded-full border border-border px-3 py-1.5 text-sm'
              }
            >
              {t(`attendance.view.${one}`)}
            </button>
          ))}
        </div>

        {view === 'ledger' ? (
          <AttendanceLedgerPanel
            from={ledgerRange.from}
            to={ledgerRange.to}
            roster={roster}
            canWrite={canWrite}
          />
        ) : null}

        {view === 'requests' ? <LeaveRequestsPanel canDecide={canWrite} /> : null}
      </div>

      {view === 'roster' ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="attendance-day">{t('attendance.dayLabel')}</Label>
              <Input
                id="attendance-day"
                type="date"
                value={day}
                onChange={(event) => {
                  setDay(event.target.value);
                  // A different day deserves a fresh list rather than however far the last one grew.
                  setPageSize(PAGE_SIZE);
                }}
              />
            </div>

            {/* A karigar sees one row — their own — so a filter over the shop's roles would be a
              control with nothing to control. */}
            {canWrite ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="attendance-role">{t('attendance.roleLabel')}</Label>
                <select
                  id="attendance-role"
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value as UserRole | '');
                    setPageSize(PAGE_SIZE);
                  }}
                  className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                >
                  <option value="">{t('attendance.allRoles')}</option>
                  {USER_ROLES.map((one) => (
                    <option key={one} value={one}>
                      {t(`role.${one}`)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          {/* Read from the day's own summary, never counted from the rows below. */}
          {canWrite && summary ? <AttendanceSummaryTiles summary={summary} /> : null}

          {attendance.isPending ? <ListSkeleton rows={4} /> : null}

          {!attendance.isPending && items.length === 0 ? (
            <EmptyState
              icon={CalendarCheck}
              title={t('attendance.emptyTitle')}
              description={t('attendance.emptyDescription')}
            />
          ) : null}

          {items.length > 0 ? (
            <AttendanceTable
              records={items}
              canWrite={canWrite}
              pendingStaffUserId={busyStaffUserId}
              onCheckIn={(record) =>
                write(
                  record.staffUserId,
                  () => checkIn.mutateAsync({ staffUserId: record.staffUserId }),
                  'attendance.checkedIn',
                )
              }
              onCheckOut={(record) =>
                write(
                  record.staffUserId,
                  () => checkOut.mutateAsync({ staffUserId: record.staffUserId }),
                  'attendance.checkedOut',
                )
              }
              onMarkLeave={setLeaveFor}
              onViewHistory={setHistoryFor}
            />
          ) : null}

          {items.length > 0 ? (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">
                {t('attendance.showing', { count: items.length, total })}
              </span>
              {items.length < total ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPageSize((current) => current + PAGE_SIZE)}
                >
                  {t('attendance.loadMore')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Mounted only for somebody who may write one, so a karigar has no leave dialog at all
          rather than one that is merely never opened. */}
      {canWrite ? (
        <MarkLeaveDialog record={leaveFor} onOpenChange={(open) => !open && setLeaveFor(null)} />
      ) : null}

      <AttendanceHistoryDialog
        record={historyFor}
        onOpenChange={(open) => !open && setHistoryFor(null)}
      />
    </>
  );
}

export default AttendancePage;
